/**
 * C-19 掲載管理（FR-15・T-D19／**改名＝D-75・T-D55**）＝発注アプリ（A-01）に出す商品の調整。
 *
 * ★**M1.5 で位置づけが変わった**（D-75）：商品を登録すれば**自動で掲載される**ので、
 * この画面は「**作らないと始まらない場所**」ではなく「**出し方を調整する場所**」になった
 * （掲載を外す・並べ替える・数量の刻みを決める）。既定カタログ（「標準カタログ」）は
 * backend が必要になった瞬間に作る＝この画面を開いた時点、または最初の商品を登録した時点。
 *
 * ★改名の理由＝**不具合 B1**：商品を30件 CSV 取込しても職人アプリが「商品がまだ登録されて
 * いません」のままだった（カタログは `catalogs`×`catalog_items` からのみ生成され、CSV に
 * カタログの概念が無く、卸開設も既定カタログを作らなかった）。「商品とは別にカタログを作る」
 * という概念そのものが原因（F1）なので、概念を既定に寄せて名前を用途どおりにした。
 *
 * **価格は持たない**（products 参照＝二重持ち禁止・04 §5）。ここで決めるのは
 * 「どの商品を・どの順で・数量ステッパーの初期値/刻みをいくつで」出すかだけ。
 *
 * 認可：カタログ本体の**新規作成は admin・更新（名称/並び/利用停止）は staff も可**（03 §7 の
 * マスタ行＝admin CRUD / staff RU）。ただし**明細（catalog_items）の追加・変更・削除は admin 限定**
 * ＝backend が upsert/delete とも ConsoleAdmin（UPSERT は追加と更新を1つのエンドポイントで
 * 兼ねるため C と U を分離できない）。**画面もそれに合わせる**＝staff に押せないボタンを出さない
 * （出すと 403 になるだけで、何が起きたか分からない）。
 */

import { auth, console_, type Catalog, type CatalogItem, type Product } from "@kenzai/api-client";
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  Table,
  Td,
} from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  adminOnlyNote,
  hintStyle,
  inputStyle,
  labelStyle,
  overlayStyle,
  warnBanner,
} from "./formStyles";

export function Catalogs({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["catalogs", "all"], queryFn: () => console_.catalogs(true) });
  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Catalog | null>(null);
  const [toggling, setToggling] = useState<Catalog | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["catalogs"] });

  const create = useMutation({
    mutationFn: (body: { name: string; note: string | null; sort_order: number }) =>
      console_.createCatalog(body),
    onSuccess: (c) => {
      setCreating(false);
      setOpenId(c.id); // 作った直後に明細を開く（空のカタログは職人に出ないため、続けて入れてもらう）
      invalidate();
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      console_.patchCatalog(id, body),
    onSettled: () => {
      setEditing(null);
      setToggling(null);
      invalidate();
    },
  });

  const rows = list.data ?? [];

  return (
    <>
      {/* ★埋め込み（商品・単価のタブ＝D-79）でも**操作は残す**＝見出しだけを親に譲る。
          PageHeader ごと隠すと「カタログを追加」まで消える（E2E が検出した）。 */}
      {embedded ? (
        <div style={{ marginBottom: "var(--kz-space-4)" }}>
          <p style={{ ...hintStyle, marginTop: 0 }}>
            登録した商品は自動で取引先の注文画面に出ます。ここでは出す/出さない・並び順・数量の刻みを
            調整します（価格は商品マスタの単価から決まります）。
            {!isAdmin && adminOnlyNote("掲載の変更")}
          </p>
          {isAdmin && (
            <button
              type="button"
              data-testid="catalog-new"
              onClick={() => setCreating(true)}
              className="kz-btn kz-btn--primary"
            >
              カタログを追加
            </button>
          )}
        </div>
      ) : (
        <PageHeader
          title="掲載管理"
          subtitle={`登録した商品は自動で取引先の注文画面に出ます。ここでは出す/出さない・並び順・数量の刻みを調整します（価格は商品マスタの単価から決まります）。${isAdmin ? "" : adminOnlyNote("掲載の変更")}`}
          actions={
            isAdmin ? (
              <button
                type="button"
                data-testid="catalog-new"
                onClick={() => setCreating(true)}
                className="kz-btn kz-btn--primary"
              >
                カタログを追加
              </button>
            ) : null
          }
        />
      )}
      {(create.isError || patch.isError) && <ErrorNote error={create.error ?? patch.error} />}

      {list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : rows.length === 0 ? (
        <Empty
          title="掲載できるカタログがありません"
          hint={
            isAdmin
              ? "すべてのカタログを利用停止にしています。再開するか、「カタログを追加」から作ってください。取引先の注文画面はここから作られます。"
              : "管理者に確認してください。"
          }
        />
      ) : (
        <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
          {rows.map((c) => (
            <CatalogCard
              key={c.id}
              catalog={c}
              isAdmin={isAdmin}
              open={openId === c.id}
              onToggleOpen={() => setOpenId(openId === c.id ? null : c.id)}
              onEdit={() => setEditing(c)}
              onToggleStatus={() => setToggling(c)}
            />
          ))}
        </div>
      )}

      {creating && (
        <CatalogForm
          title="カタログを追加"
          busy={create.isPending}
          onSubmit={(v) => create.mutate(v)}
          onCancel={() => setCreating(false)}
        />
      )}
      {editing && (
        <CatalogForm
          title="カタログを編集"
          initial={editing}
          busy={patch.isPending}
          onSubmit={(v) => patch.mutate({ id: editing.id, body: { ...v, updated_at: editing.updated_at } })}
          onCancel={() => setEditing(null)}
        />
      )}
      {toggling && (
        <ConfirmDialog
          title={
            toggling.status === "active"
              ? "このカタログを利用停止にしますか？"
              : "このカタログの利用を再開しますか？"
          }
          body={
            toggling.status === "active"
              ? "取引先の注文画面から消えます。すでに入っている注文はそのまま残ります。"
              : "取引先の注文画面に戻ります。"
          }
          confirmLabel={toggling.status === "active" ? "利用停止にする" : "利用を再開する"}
          busy={patch.isPending}
          onConfirm={() =>
            patch.mutate({
              id: toggling.id,
              body: {
                status: toggling.status === "active" ? "disabled" : "active",
                updated_at: toggling.updated_at,
              },
            })
          }
          onCancel={() => setToggling(null)}
        />
      )}
    </>
  );
}

function CatalogCard({
  catalog,
  isAdmin,
  open,
  onToggleOpen,
  onEdit,
  onToggleStatus,
}: {
  catalog: Catalog;
  isAdmin: boolean;
  open: boolean;
  onToggleOpen: () => void;
  onEdit: () => void;
  onToggleStatus: () => void;
}) {
  const items = useQuery({
    queryKey: ["catalog-items", catalog.id],
    queryFn: () => console_.catalogItems(catalog.id),
  });
  const count = items.data?.length;

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-3)", flexWrap: "wrap" }}>
        <strong style={{ fontSize: "var(--kz-font-size-lg)" }}>{catalog.name}</strong>
        {catalog.status === "active" ? <Badge tone="primary">利用中</Badge> : <Badge>利用停止</Badge>}
        {count !== undefined && (
          <Badge tone={count === 0 ? "warn" : undefined}>商品 {count} 点</Badge>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: "var(--kz-space-2)" }}>
          <button
            type="button"
            data-testid={`catalog-items-${catalog.id}`}
            onClick={onToggleOpen}
            className="kz-btn"
          >
            {open ? "商品を閉じる" : "商品を選ぶ"}
          </button>
          <button type="button" data-testid={`catalog-edit-${catalog.id}`} onClick={onEdit} className="kz-btn">
            編集
          </button>
          <button
            type="button"
            data-testid={`catalog-toggle-${catalog.id}`}
            onClick={onToggleStatus}
            className="kz-btn"
          >
            {catalog.status === "active" ? "利用停止にする" : "利用を再開する"}
          </button>
        </div>
      </div>
      {catalog.note && (
        <div style={{ marginTop: "var(--kz-space-2)", color: "var(--kz-color-ink-muted)" }}>{catalog.note}</div>
      )}
      {/* 明細ゼロのカタログは職人の画面に出ない（backend の catalog_tree が返さない）。
          自動掲載（D-75）が入った M1.5 以降、この状態は「掲載を全部外した」ことを意味する
          ＝意図的なのか事故なのかは画面から区別できないので、警告は残す。 */}
      {count === 0 && (
        <div data-testid={`catalog-empty-${catalog.id}`} style={{ ...warnBanner, margin: "var(--kz-space-3) 0 0" }}>
          このカタログに商品が1点も入っていません（掲載を全部外した状態）。このままでは取引先の注文画面に表示されません。
        </div>
      )}
      {open && <CatalogItems catalogId={catalog.id} isAdmin={isAdmin} items={items.data ?? []} />}
    </Card>
  );
}

function CatalogItems({
  catalogId,
  isAdmin,
  items,
}: {
  catalogId: number;
  isAdmin: boolean;
  items: CatalogItem[];
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [removing, setRemoving] = useState<CatalogItem | null>(null);

  const products = useQuery({
    queryKey: ["products", "catalog-picker", q],
    queryFn: () => console_.products(`?limit=50${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["catalog-items", catalogId] });
    qc.invalidateQueries({ queryKey: ["catalogs"] });
  };

  const upsert = useMutation({
    mutationFn: (body: { product_id: number; sort_order: number; qty_start: number; qty_step: number }) =>
      console_.upsertCatalogItem(catalogId, body),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (item: CatalogItem) => console_.deleteCatalogItem(catalogId, item.id),
    onSettled: () => {
      setRemoving(null);
      refresh();
    },
  });

  const byProduct = new Map(items.map((i) => [i.product_id, i]));
  const nameOf = (productId: number) =>
    products.data?.items.find((p: Product) => p.id === productId)?.name;

  return (
    <div style={{ marginTop: "var(--kz-space-4)", display: "grid", gap: "var(--kz-space-4)" }}>
      {(upsert.isError || remove.isError) && <ErrorNote error={upsert.error ?? remove.error} />}

      <div>
        <h3 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
          このカタログの商品
        </h3>
        {items.length === 0 ? (
          <div style={hintStyle}>下の一覧から商品を追加してください。</div>
        ) : (
          <Card padding="0">
            <Table head={["並び", "商品", "数量の初期値", "刻み", "操作"]}>
              {items.map((it) => (
                <tr key={it.id} data-testid={`catalog-item-${it.id}`}>
                  <Td>
                    <NumberCell
                      testid={`item-sort-${it.id}`}
                      readOnly={!isAdmin}
                      value={it.sort_order}
                      onCommit={(v) =>
                        upsert.mutate({
                          product_id: it.product_id,
                          sort_order: v,
                          qty_start: it.qty_start,
                          qty_step: it.qty_step,
                        })
                      }
                    />
                  </Td>
                  <Td style={{ fontWeight: 600 }}>{nameOf(it.product_id) ?? `商品 #${it.product_id}`}</Td>
                  <Td>
                    <NumberCell
                      testid={`item-start-${it.id}`}
                      readOnly={!isAdmin}
                      value={it.qty_start}
                      min={1}
                      onCommit={(v) =>
                        upsert.mutate({
                          product_id: it.product_id,
                          sort_order: it.sort_order,
                          qty_start: v,
                          qty_step: it.qty_step,
                        })
                      }
                    />
                  </Td>
                  <Td>
                    <NumberCell
                      testid={`item-step-${it.id}`}
                      readOnly={!isAdmin}
                      value={it.qty_step}
                      min={1}
                      onCommit={(v) =>
                        upsert.mutate({
                          product_id: it.product_id,
                          sort_order: it.sort_order,
                          qty_start: it.qty_start,
                          qty_step: v,
                        })
                      }
                    />
                  </Td>
                  <Td>
                    {isAdmin && (
                      <button
                        type="button"
                        data-testid={`item-remove-${it.id}`}
                        onClick={() => setRemoving(it)}
                        className="kz-btn kz-btn--sm"
                      >
                        外す
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
      </div>

      {!isAdmin ? (
        <div style={hintStyle}>商品の追加・変更は管理者のみが行えます。</div>
      ) : (
      <div>
        <h3 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>商品を追加</h3>
        <input
          type="search"
          data-testid="catalog-product-search"
          placeholder="商品名・品番で検索"
          onKeyDown={(e) => e.key === "Enter" && setQ((e.target as HTMLInputElement).value)}
          style={{ ...inputStyle, width: 240 }}
        />
        {products.isLoading ? (
          <Spinner />
        ) : (
          <Card padding="0" style={{ marginTop: "var(--kz-space-2)" }}>
            <Table head={["品番", "商品", "単位", "", ""]}>
              {(products.data?.items ?? []).map((p: Product) => {
                const already = byProduct.get(p.id);
                return (
                  <tr key={p.id} data-testid={`pick-${p.id}`}>
                    <Td>
                      <span className="kz-num">{p.display_no}</span>
                    </Td>
                    <Td style={{ fontWeight: 600 }}>{p.name}</Td>
                    <Td>{p.unit}</Td>
                    <Td>{p.status === "disabled" && <Badge tone="warn">廃番</Badge>}</Td>
                    <Td>
                      {already ? (
                        <span style={hintStyle}>追加済み</span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`pick-add-${p.id}`}
                          disabled={p.status === "disabled"}
                          onClick={() =>
                            upsert.mutate({
                              product_id: p.id,
                              sort_order: items.length,
                              qty_start: 1,
                              qty_step: 1,
                            })
                          }
                          className="kz-btn kz-btn--sm"
                        >
                          追加する
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Table>
          </Card>
        )}
      </div>
      )}

      {removing && (
        <ConfirmDialog
          title="この商品をカタログから外しますか？"
          body="取引先の注文画面に出なくなります。商品マスタからは消えません。"
          confirmLabel="外す"
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/** 数値セル＝**Enter か blur で確定**（1文字打つたびに API を叩かない）。 */
function NumberCell({
  testid,
  value,
  min,
  readOnly,
  onCommit,
}: {
  testid: string;
  value: number;
  min?: number;
  readOnly?: boolean;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const commit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || (min !== undefined && n < min) || n === value) {
      setDraft(String(value));
      return;
    }
    onCommit(n);
  };
  if (readOnly) return <span className="kz-num">{value}</span>;
  return (
    <input
      type="number"
      data-testid={testid}
      value={draft}
      min={min}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      style={{ ...inputStyle, width: 96, minHeight: 36 }}
    />
  );
}

function CatalogForm({
  title,
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: Catalog;
  busy: boolean;
  onSubmit: (v: { name: string; note: string | null; sort_order: number }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [sortOrder, setSortOrder] = useState(String(initial?.sort_order ?? 0));

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label={title}>
      <Card>
        <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-lg)" }}>{title}</h2>
        <div style={{ display: "grid", gap: "var(--kz-space-3)", marginTop: "var(--kz-space-4)" }}>
          <label style={labelStyle}>
            カタログ名
            <input
              data-testid="catalog-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ボード・木材"
              maxLength={100}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            メモ
            <input
              data-testid="catalog-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={inputStyle}
            />
            <span style={hintStyle}>社内向けの覚え書き。取引先には出ません。</span>
          </label>
          <label style={labelStyle}>
            並び順
            <input
              type="number"
              data-testid="catalog-sort"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>
        <div
          style={{
            display: "flex",
            gap: "var(--kz-space-2)",
            marginTop: "var(--kz-space-5)",
            justifyContent: "flex-end",
          }}
        >
          <button type="button" onClick={onCancel} className="kz-btn">
            やめる
          </button>
          <button
            type="button"
            data-testid="catalog-save"
            disabled={busy || name.trim() === ""}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                note: note.trim() === "" ? null : note.trim(),
                sort_order: Number(sortOrder) || 0,
              })
            }
            className="kz-btn kz-btn--primary"
          >
            保存する
          </button>
        </div>
      </Card>
    </div>
  );
}
